import { useCallback, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { i18n } from 'builder/utils/i18n'
import { formatDate } from 'builder/utils/formatDate'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useClickOutside } from 'builder/hooks/useClickOutside'
import { BriefExpertDoc, BriefResume } from 'builder/modules/panel'
import { DocumentPreview } from 'builder/components/Panel/Document/styles'
import { formatDocumentPreviewUrl } from 'builder/utils/formatDocumentPreviewUrl'
import { ReplaceResumePopup } from 'builder/views/ResumeDistributionDashboardView/components/DashboardHeader/ReplaceResumePopup/ReplaceResumePopup'
import {
  DESKTOP_PREVIEW_WIDTH,
  PHONE_PREVIEW_WIDTH,
} from 'builder/components/Panel/Document/constants'
import { DocumentTypes } from 'builder/modules/constants'

import { actions as resumeDistributionActions } from 'builder/modules/resumeDistribution'
import Icon24 from 'builder/components/Icon'
import { actions } from 'builder/modules/resumeEditor'
import FieldSpinnerLoader from '../../FormLayout/FieldSpinnerLoader'
import {
  DocumentPreviewContainer,
  ResumeBuilderPopupContainer,
  ResumeSelectionContainer,
  RightBottonShadow,
  SelectLeft,
  SelectResumeDate,
  SelectResumeTitle,
  SelectRight,
  ActionContainer,
  StyledActionButton,
} from '../styles'
import SelectResumeModal from '../../SelectResumeModal'

type Props = {
  resumePayload: BriefResume | BriefExpertDoc | null
  isParserLoading: boolean
  handleFileInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleBuilderResumeChange: (data: BriefResume | BriefExpertDoc) => void
}

const V3_TRANSLATION = 'builder.resume_distribution.version_3.questionnaire.resume_preview_box'

export const DATA_HIGHLIGHT_ID = 'resume-distribution-change-resume-action'

export const ResumeBuilderPreview = ({
  resumePayload,
  isParserLoading,
  handleFileInputChange,
  handleBuilderResumeChange,
}: Props) => {
  const dispatch = useDispatch()
  const { isPhone } = useMediaQueries()
  const [showPopup, setShowPopup] = useState(false)
  const [showResumeSelection, setResumeSelection] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  useClickOutside(containerRef, () => setShowPopup(false))

  const previewUrl = useCallback(
    (document: BriefResume | null) => {
      return formatDocumentPreviewUrl(document as BriefResume, {
        size: isPhone ? PHONE_PREVIEW_WIDTH : DESKTOP_PREVIEW_WIDTH,
      })
    },
    [isPhone],
  )

  const getThumbnailImg = () => {
    if (resumePayload?.type === DocumentTypes.trDocument && resumePayload?.thumbnail?.url) {
      return resumePayload?.thumbnail?.url
    }
    return previewUrl(resumePayload as BriefResume)
  }

  const getDocumentName = () => {
    if (resumePayload?.type === DocumentTypes.trDocument && resumePayload?.title) {
      return resumePayload?.title
    }

    const name = resumePayload?.name || i18n.t('builder.dashboard.untitled')
    return resumePayload?.jobTitle ? `${name}, ${resumePayload?.jobTitle}` : name
  }

  const formatUpdateDate = () => {
    const date = resumePayload?.updatedAt || resumePayload?.createdAt || new Date().toISOString()
    return formatDate(new Date(date), 'dd MMM, HH:mm')
  }

  const handleOpenPreview = () => {
    dispatch(resumeDistributionActions.setResumePreviewModalStatus(true))
    trackInternalEvent('click_preview_resume', {
      label: 'rd_form',
    })
  }

  const handleChangeResumeClick = useCallback(() => {
    setShowPopup(!showPopup)
    dispatch(actions.setHighlightedElement(null))
    trackInternalEvent('click_change_resume', {
      label: 'rd_form',
    })
  }, [dispatch, showPopup])

  return (
    <ResumeSelectionContainer>
      <SelectLeft>
        <SelectResumeTitle>{getDocumentName()}</SelectResumeTitle>
        <SelectResumeDate>
          {i18n.t(`builder.resume_distribution.landing.modal.updated_at`, {
            dateString: formatUpdateDate(),
          })}
        </SelectResumeDate>
        <ActionContainer>
          <StyledActionButton
            theme="ghost"
            size="small"
            leadingIcon={<Icon24.View />}
            onClick={handleOpenPreview}
          >
            {i18n.t(`${V3_TRANSLATION}.preview_button_text`)}
          </StyledActionButton>
          <StyledActionButton
            theme="ghost"
            size="small"
            onClick={handleChangeResumeClick}
            data-highlight-id={DATA_HIGHLIGHT_ID}
          >
            {isParserLoading ? (
              <FieldSpinnerLoader />
            ) : isPhone ? (
              i18n.t(`${V3_TRANSLATION}.change_resume_mobile_button_text`)
            ) : (
              i18n.t(`${V3_TRANSLATION}.change_resume_button_text`)
            )}
          </StyledActionButton>
        </ActionContainer>

        {showPopup && (
          <ResumeBuilderPopupContainer>
            <ReplaceResumePopup
              setResumeSelection={value => {
                setResumeSelection(value)
                setShowPopup(false)
              }}
              handleFileInputChange={handleFileInputChange}
              ref={containerRef}
            />
          </ResumeBuilderPopupContainer>
        )}

        {showResumeSelection && (
          <SelectResumeModal
            onClose={() => setResumeSelection(false)}
            submitSelectedResume={handleBuilderResumeChange}
          />
        )}
      </SelectLeft>
      <SelectRight>
        <DocumentPreviewContainer>
          {resumePayload && (
            <DocumentPreview showUnavailablePreview={false} src={getThumbnailImg()} />
          )}
        </DocumentPreviewContainer>
        <RightBottonShadow />
      </SelectRight>
    </ResumeSelectionContainer>
  )
}
